import React, { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LayoutDashboard, Landmark, Users, Store, Network } from 'lucide-react';
import {
  PageHeader,
  PeriodPicker,
  DashboardRangeBar,
  TimezoneToggle,
  BROWSER_TIMEZONE,
  parseDashboardPeriod,
} from '@kioskgaming/ui';
import AdminTabs from '../../ui/AdminTabs';
import { TooltipProvider } from '../../ui/shadcn';
import type { DashboardPeriod, DashboardPeriodParams, DashboardV2Envelope } from '../../../types';
import {
  useDashboardV2AgentNetwork,
  useDashboardV2Executive,
  useDashboardV2Overview,
  useDashboardV2PlatformPlayers,
  useDashboardV2ShopPlayers,
} from '../../../hooks/useDashboardV2';
import RollupLagNotice from './RollupLagNotice';
import OverviewTab from './OverviewTab';
import FinancialsTab from './FinancialsTab';
import PlatformPlayersTab from './PlatformPlayersTab';
import ShopPlayersTab from './ShopPlayersTab';
import AgentNetworkTab from './AgentNetworkTab';

type DashboardTab =
  | 'overview'
  | 'financials'
  | 'platform-players'
  | 'shop-players'
  | 'agent-network';

const TABS = [
  { id: 'overview' as const, label: 'Overview', icon: LayoutDashboard },
  { id: 'financials' as const, label: 'Financials', icon: Landmark },
  { id: 'platform-players' as const, label: 'Platform-Players', icon: Users },
  { id: 'shop-players' as const, label: 'Shop-Players', icon: Store },
  { id: 'agent-network' as const, label: 'Agent Network', icon: Network },
];

const VALID_TABS = new Set<string>(TABS.map((t) => t.id));

function parseTab(raw: string | null): DashboardTab {
  return VALID_TABS.has(raw || '') ? (raw as DashboardTab) : 'overview';
}

function parsePeriodParam(raw: string | null): DashboardPeriod {
  return parseDashboardPeriod(raw) as DashboardPeriod;
}

/** Main dashboard page: tab + period state synced to URL search params. */
const DashboardPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = parseTab(searchParams.get('tab'));
  const useLocalTz = searchParams.get('tz') === 'local';
  const periodParams: DashboardPeriodParams = useMemo(() => {
    const period = parsePeriodParam(searchParams.get('period'));
    const dateFrom = searchParams.get('from') || undefined;
    const dateTo = searchParams.get('to') || undefined;
    return {
      period,
      ...(period === 'custom' && dateFrom ? { dateFrom } : {}),
      ...(period === 'custom' && dateTo ? { dateTo } : {}),
      ...(useLocalTz ? { tz: BROWSER_TIMEZONE } : {}),
    };
  }, [searchParams, useLocalTz]);

  // Each query is gated on its own tab so exactly one request is in flight.
  const executiveQuery = useDashboardV2Executive(periodParams, activeTab === 'overview');
  const financialsQuery = useDashboardV2Overview(periodParams, activeTab === 'financials');
  const platformPlayersQuery = useDashboardV2PlatformPlayers(
    periodParams,
    activeTab === 'platform-players',
  );
  const shopPlayersQuery = useDashboardV2ShopPlayers(periodParams, activeTab === 'shop-players');
  const agentNetworkQuery = useDashboardV2AgentNetwork(periodParams, activeTab === 'agent-network');

  const activeQuery =
    activeTab === 'overview'
      ? executiveQuery
      : activeTab === 'financials'
        ? financialsQuery
        : activeTab === 'platform-players'
          ? platformPlayersQuery
          : activeTab === 'shop-players'
            ? shopPlayersQuery
            : agentNetworkQuery;

  const setTab = useCallback(
    (tab: DashboardTab) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', tab);
        return next;
      });
    },
    [setSearchParams],
  );

  const setPeriodParams = useCallback(
    (params: DashboardPeriodParams) => {
      // Custom without dates must never hit the URL/API (backend 400).
      if (params.period === 'custom' && (!params.dateFrom || !params.dateTo)) return;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('period', params.period);
        if (params.period === 'custom' && params.dateFrom && params.dateTo) {
          next.set('from', params.dateFrom);
          next.set('to', params.dateTo);
        } else {
          next.delete('from');
          next.delete('to');
        }
        return next;
      });
    },
    [setSearchParams],
  );

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6 rounded-2xl">
        <PageHeader
          title="Dashboard"
          description="Platform pulse, player activity, and agent network performance"
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <RollupLagNotice rollup={(activeQuery.data as DashboardV2Envelope | undefined)?.rollup} />
              <PeriodPicker
                value={periodParams.period}
                dateFrom={periodParams.dateFrom}
                dateTo={periodParams.dateTo}
                onChange={(next) =>
                  setPeriodParams({
                    period: next.period,
                    dateFrom: next.dateFrom,
                    dateTo: next.dateTo,
                    ...(useLocalTz ? { tz: BROWSER_TIMEZONE } : {}),
                  })
                }
                accent="blue"
              />
              <TimezoneToggle
                accent="blue"
                defaultLabel="Platform time"
                useLocal={useLocalTz}
                onChange={(local) => {
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    if (local) next.set('tz', 'local');
                    else next.delete('tz');
                    return next;
                  });
                }}
              />
            </div>
          }
        />

        <DashboardRangeBar
          envelope={activeQuery.data as DashboardV2Envelope | undefined}
          onRefresh={activeQuery.refresh}
          refreshing={activeQuery.refreshing || activeQuery.isFetching}
          accent="blue"
        />

        <AdminTabs
          tabs={TABS}
          value={activeTab}
          onChange={setTab}
          variant="underline"
          aria-label="Dashboard sections"
        />

        {activeTab === 'overview' && (
          <OverviewTab
            data={executiveQuery.data}
            isLoading={executiveQuery.isLoading}
            error={executiveQuery.error}
            onRetry={() => executiveQuery.refetch()}
          />
        )}
        {activeTab === 'financials' && (
          <FinancialsTab
            data={financialsQuery.data}
            isLoading={financialsQuery.isLoading}
            error={financialsQuery.error}
            onRetry={() => financialsQuery.refetch()}
          />
        )}
        {activeTab === 'platform-players' && (
          <PlatformPlayersTab
            data={platformPlayersQuery.data}
            isLoading={platformPlayersQuery.isLoading}
            error={platformPlayersQuery.error}
            onRetry={() => platformPlayersQuery.refetch()}
          />
        )}
        {activeTab === 'shop-players' && (
          <ShopPlayersTab
            data={shopPlayersQuery.data}
            isLoading={shopPlayersQuery.isLoading}
            error={shopPlayersQuery.error}
            onRetry={() => shopPlayersQuery.refetch()}
          />
        )}
        {activeTab === 'agent-network' && (
          <AgentNetworkTab
            data={agentNetworkQuery.data}
            isLoading={agentNetworkQuery.isLoading}
            error={agentNetworkQuery.error}
            onRetry={() => agentNetworkQuery.refetch()}
          />
        )}
      </div>
    </TooltipProvider>
  );
};

export default DashboardPage;
